<template>
    <teleport to="body">
        <div v-if="visible" ref="popupContainer" class="ans-wrapper-teleport-container" :style="positionStyle">
            <slot />
        </div>
    </teleport>
</template>

<script>
/**
 * ****************************************************************************
 * @description     :   Generic Teleport wrapper for Popup Components
 * @created at      :   2025/09/29
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

export default {
    name: 'ansWrapper',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        triggerElement: {
            type: Object,
            default: null
        },
        closeOnClickOutside: {
            type: Boolean,
            default: true
        },
        closeOnEscape: {
            type: Boolean,
            default: true
        },
        popupWidth: {
            type: Number,
            default: 320
        },
        popupHeight: {
            type: Number,
            default: 300
        }
    },
    emits: ['close', 'select'],
    data() {
        return {
            positionStyle: {}
        };
    },
    watch: {
        visible: {
            handler(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.calculatePosition();
                        this.addClickOutsideListener();
                    });
                } else {
                    this.removeClickOutsideListener();
                }
            },
            immediate: true
        }
    },
    beforeUnmount() {
        this.removeClickOutsideListener();
    },
    methods: {
        calculatePosition() {
            if (!this.triggerElement) return;

            const rect = this.triggerElement.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

            const popupWidth = this.$refs.popupContainer.offsetWidth;
            const popupHeight = this.$refs.popupContainer.offsetHeight;

            // Calculate left so that popup is centered horizontally to triggerElement
            let left = rect.left + scrollLeft + rect.width / 2 - popupWidth / 2;

            // Position below the trigger element
            let top = rect.bottom + scrollTop + 5;

            // Ensure popup doesn't go outside viewport
            // Check right boundary
            if (left + popupWidth > window.innerWidth) {
                left = window.innerWidth - popupWidth - 10;
            }

            // Check left boundary
            if (left < 10) {
                left = 10;
            }

            // Check bottom boundary
            if (top + popupHeight > window.innerHeight + scrollTop) {
                top = rect.top + scrollTop - popupHeight - 5;
            }

            // Check top boundary
            if (top < scrollTop + 10) {
                top = scrollTop + 10;
            }

            this.positionStyle = {
                position: 'absolute',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 9999
            };
        },

        addClickOutsideListener() {
            // Add listener with slight delay to prevent immediate closing
            setTimeout(() => {
                if (this.closeOnClickOutside) {
                    document.addEventListener('click', this.handleClickOutside, true);
                }
                if (this.closeOnEscape) {
                    document.addEventListener('keydown', this.handleKeydown);
                }
            }, 100);
        },

        removeClickOutsideListener() {
            document.removeEventListener('click', this.handleClickOutside, true);
            document.removeEventListener('keydown', this.handleKeydown);
        },

        handleClickOutside(event) {
            if (!this.visible) return;

            const popupContainer = this.$refs.popupContainer;
            const triggerElement = this.triggerElement;

            // Check if click is outside popup and trigger element
            if (popupContainer && !popupContainer.contains(event.target) && triggerElement && !triggerElement.contains(event.target)) {
                this.handleClose();
            }
        },

        handleKeydown(event) {
            if (event.key === 'Escape' && this.visible) {
                this.handleClose();
            }
        },

        handleClose() {
            this.$emit('close');
        }
    }
};
</script>

<style lang="scss" scoped>
.ans-wrapper-teleport-container {
    position: absolute;
    z-index: 9999;
}
</style>
